import { toISODate } from './date';
import { dateIsoForMondayWeekday, parseAnnualCellKey } from './annualTimetable';
import type { ClassScheduleRow } from '../services/sectionLayoutService';

function minutesFromHHMM(hhmm: string | null | undefined): number | null {
  const value = (hhmm ?? '').trim();
  if (!value) return null;
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

function getCurrentClassSlotIndex(sectionRows: ClassScheduleRow[]): number | null {
  if (!sectionRows.length) return null;
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  for (let slotIndex = 0; slotIndex < sectionRows.length; slotIndex += 1) {
    const row = sectionRows[slotIndex];
    if (!row || row.kind !== 'class') continue;
    const start = minutesFromHHMM(row.startTime);
    const end = minutesFromHHMM(row.endTime);
    if (start == null || end == null) continue;
    if (nowMinutes >= start && nowMinutes < end) return slotIndex;
  }
  return null;
}

function getCurrentOrUpcomingTodayRowIndex(
  sectionRows: ClassScheduleRow[],
  todayRows: { annualCellKey: string; slotIndex: number }[]
): number | null {
  if (!sectionRows.length || !todayRows.length) return null;
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  let upcomingIndex: number | null = null;
  let upcomingStartMinutes: number | null = null;
  for (let idx = 0; idx < todayRows.length; idx += 1) {
    const slotIndex = todayRows[idx]?.slotIndex ?? -1;
    if (slotIndex < 0) continue;
    const row = sectionRows[slotIndex];
    if (!row || row.kind !== 'class') continue;
    const start = minutesFromHHMM(row.startTime);
    const end = minutesFromHHMM(row.endTime);
    if (start == null || end == null) continue;
    if (nowMinutes >= start && nowMinutes < end) return idx;
    if (start > nowMinutes && (upcomingStartMinutes == null || start < upcomingStartMinutes)) {
      upcomingIndex = idx;
      upcomingStartMinutes = start;
    }
  }
  return upcomingIndex;
}

export type PickTodayLessonPickMode = 'cycle' | 'clock_only';

/**
 * 오늘 날짜에 해당하는 연간 셀 키 하나를 고른다. (연간진도표 / 프로그램 테이블 공통)
 * `sectionRows`가 비면 시각 매칭·순회 fallback은 생략되고 첫 오늘 행이 기본이 된다.
 *
 * - `cycle`: `lastTodaySelectionKey`가 오늘 행 중 하나이면 그다음 교시로 순환, 아니면 현재 시각(또는 곧 시작 교시) 기준.
 * - `clock_only`: 항상 현재 시각 매칭만(반복 단축키용). `lastTodaySelectionKey`는 무시.
 */
export function pickTodayLessonAnnualCellKey(
  tableRows: Array<{ annualCellKey: string }>,
  sectionRows: ClassScheduleRow[] | undefined | null,
  options?: { lastTodaySelectionKey?: string | null; pickMode?: PickTodayLessonPickMode }
): string | null {
  const sec = sectionRows ?? [];
  const todayIso = toISODate(new Date());
  const todayRows: { annualCellKey: string; slotIndex: number }[] = [];
  tableRows.forEach((row) => {
    const parsed = parseAnnualCellKey(row.annualCellKey);
    if (!parsed) return;
    const dateIso = dateIsoForMondayWeekday(parsed.mondayIso, parsed.weekdayKey);
    if (dateIso !== todayIso) return;
    todayRows.push({ annualCellKey: row.annualCellKey, slotIndex: parsed.slotIndex });
  });
  if (todayRows.length === 0) return null;
  todayRows.sort((a, b) => a.slotIndex - b.slotIndex);

  const pickMode: PickTodayLessonPickMode = options?.pickMode ?? 'cycle';

  const clockOnlyIndex = (): number => {
    const preferredIndex = sec.length ? getCurrentOrUpcomingTodayRowIndex(sec, todayRows) : null;
    let nextIndex = preferredIndex ?? 0;
    if (preferredIndex == null) {
      const currentSlot = getCurrentClassSlotIndex(sec);
      if (currentSlot != null) {
        const found = todayRows.findIndex((entry) => entry.slotIndex === currentSlot);
        if (found >= 0) nextIndex = found;
      }
    }
    return nextIndex;
  };

  if (pickMode === 'clock_only') {
    const nextIndex = clockOnlyIndex();
    return todayRows[nextIndex]?.annualCellKey ?? null;
  }

  const lastTodaySelectionKey = options?.lastTodaySelectionKey;
  if (lastTodaySelectionKey) {
    const idx = todayRows.findIndex((entry) => entry.annualCellKey === lastTodaySelectionKey);
    if (idx >= 0) {
      return todayRows[(idx + 1) % todayRows.length].annualCellKey;
    }
  }

  const nextIndex = clockOnlyIndex();
  return todayRows[nextIndex]?.annualCellKey ?? null;
}

/**
 * 기준 날짜(`targetDateIso`) 행을 우선 선택하고, 없으면 가장 가까운 미래 날짜 행을 선택한다.
 * 같은 날짜에 여러 교시가 있으면 `lastSelectionKey` 기준으로 순환한다.
 */
export function pickDateOrNearestFutureAnnualCellKey(
  tableRows: Array<{ annualCellKey: string }>,
  targetDateIso: string,
  options?: { lastSelectionKey?: string | null }
): string | null {
  const rowsByDate = new Map<string, { annualCellKey: string; slotIndex: number }[]>();
  tableRows.forEach((row) => {
    const parsed = parseAnnualCellKey(row.annualCellKey);
    if (!parsed) return;
    const dateIso = dateIsoForMondayWeekday(parsed.mondayIso, parsed.weekdayKey);
    const list = rowsByDate.get(dateIso) ?? [];
    list.push({ annualCellKey: row.annualCellKey, slotIndex: parsed.slotIndex });
    rowsByDate.set(dateIso, list);
  });
  if (rowsByDate.size === 0) return null;

  let targetRows = rowsByDate.get(targetDateIso) ?? null;
  if (!targetRows || targetRows.length === 0) {
    const nearestFutureDateIso = Array.from(rowsByDate.keys())
      .filter((d) => d > targetDateIso)
      .sort((a, b) => a.localeCompare(b))[0];
    if (!nearestFutureDateIso) return null;
    targetRows = rowsByDate.get(nearestFutureDateIso) ?? null;
  }
  if (!targetRows || targetRows.length === 0) return null;

  targetRows.sort((a, b) => a.slotIndex - b.slotIndex);
  let nextIndex = 0;
  const lastSelectionKey = options?.lastSelectionKey ?? null;
  if (lastSelectionKey) {
    const idx = targetRows.findIndex((entry) => entry.annualCellKey === lastSelectionKey);
    if (idx >= 0) nextIndex = (idx + 1) % targetRows.length;
  }
  return targetRows[nextIndex]?.annualCellKey ?? null;
}

/**
 * 오늘(시각/순회) 셀을 먼저 고르고, 오늘 행이 전혀 없으면 `targetDateIso`(기본: 오늘) 기준 가장 가까운 미래 첫 수업 셀.
 * — 연간진도표 `openToday`·「오늘 학습내용」버튼·Shift+R(동일 `openToday`)에 공통 사용.
 */
export function pickTodayOrNearestFutureForLesson(
  tableRows: Array<{ annualCellKey: string }>,
  sectionRows: ClassScheduleRow[] | undefined | null,
  options?: { lastTodaySelectionKey?: string | null; pickMode?: PickTodayLessonPickMode; targetDateIso?: string }
): string | null {
  const a = pickTodayLessonAnnualCellKey(tableRows, sectionRows, {
    lastTodaySelectionKey: options?.lastTodaySelectionKey,
    pickMode: options?.pickMode,
  });
  if (a) return a;
  const target = (options?.targetDateIso ?? toISODate(new Date())).trim() || toISODate(new Date());
  return pickDateOrNearestFutureAnnualCellKey(tableRows, target);
}
